import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["scrollDirection"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITable from 'UIComponents/table/UITable';
import { SuccessIcon } from 'foundations-assets';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Image } from 'foundations-components/transitional/components/Image';
import { Link } from 'foundations-components/transitional/components/Link';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { useMemo } from 'react';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import styled, { css, useTheme } from 'styled-components';
import { CRM as CRM_ICON, ProductLinePageIcons } from 'ui-addon-upgrades/_core/productPage/ProductPageIcons';
import * as TierGroups from '../../constants/groupTags/TierGroups';
import * as Tier from '../../constants/groupTags/Tiers';
import { getTierHubI18nKey, getTierI18nKey, getTierLevelI18nKey } from '../../utils/i18n';
import { getPricingUrl } from '../../utils/urls';
import { FLATTENED_TIER_ORDER } from '../constants';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TierGroupIcons = {
  CRM: CRM_ICON,
  MARKETING: ProductLinePageIcons[ProductLines.MARKETING],
  SALES: ProductLinePageIcons[ProductLines.SALES],
  SERVICE: ProductLinePageIcons[ProductLines.SERVICE],
  CMS: ProductLinePageIcons[ProductLines.CMS],
  CONTENT: ProductLinePageIcons[ProductLines.CMS],
  OPERATIONS: ProductLinePageIcons[ProductLines.OPERATIONS]
};
const ScrollContainerComponent = _ref => {
  let {
      scrollDirection
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(ScrollContainer, Object.assign({}, rest, {
    size: 40,
    blockSize: scrollDirection === 'both' ? '100%' : 'initial'
  }));
};
export default function FeatureCompatibilityMatrix({
  featuresWithTierCompatibility,
  featureType,
  isCustomer,
  portalTiers,
  tierGroupNames,
  trackPricingLinkClick
}) {
  const theme = useTheme();
  const borderStyles = css(["border-left:", ";"], props => props.showBorder ? `1px solid ${theme.color['border-primary-subtle']} !important` : 0);
  const TR = styled.tr.withConfig({
    displayName: "FeatureCompatibilityMatrix__TR",
    componentId: "prh3dn-0"
  })(["background-color:", " !important;&:hover{background-color:", " !important;}"], props => props.index % 2 === 0 ? theme.color['fill-tertiary-hover'] : theme.color['text-core-on-fill-default'], props => props.index % 2 === 0 ? theme.color['fill-tertiary-hover'] : theme.color['text-core-on-fill-default']);
  TR.displayName = 'tr';
  const TH = styled.th.withConfig({
    displayName: "FeatureCompatibilityMatrix__TH",
    componentId: "prh3dn-1"
  })(["", ";text-transform:uppercase;font-weight:600;font-size:12px;padding:12px;"], borderStyles);
  const TD = styled.td.withConfig({
    displayName: "FeatureCompatibilityMatrix__TD",
    componentId: "prh3dn-2"
  })(["", ";height:", ";"], borderStyles, props => props.compact ? '48px !important' : 'inherit');
  TD.displayName = 'td';
  const renderedHubsHeader = /*#__PURE__*/_jsxs(TR, {
    index: 0,
    style: {
      whiteSpace: 'nowrap',
      fontWeight: 600
    },
    children: [/*#__PURE__*/_jsx(TD, {}), tierGroupNames.map(tierGroup => /*#__PURE__*/_jsx(TD, {
      colSpan: TierGroups[tierGroup].length,
      children: /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        justify: "center",
        children: [/*#__PURE__*/_jsx(Image, {
          display: "inline-block",
          marginInlineEnd: "200",
          src: TierGroupIcons[tierGroup],
          inlineSize: "16px"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: getTierHubI18nKey(tierGroup)
        })]
      })
    }, tierGroup))]
  });
  const renderedHubLevels = useMemo(() => /*#__PURE__*/_jsxs(TR, {
    index: 1,
    compact: true,
    style: {
      whiteSpace: 'nowrap',
      height: 48
    },
    children: [/*#__PURE__*/_jsx(TH, {
      style: {
        backgroundColor: 'inherit',
        boxShadow: 'inset -3px 0 3px -3px #cbd6e2',
        left: '0',
        position: 'sticky',
        textAlign: 'start',
        paddingLeft: '24px'
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'marketplace-ui-common.compatibility.featureCompatibilityMatrix.features',
        options: {
          type: featureType
        }
      })
    }), tierGroupNames.map(tierGroup => TierGroups[tierGroup].map((tier, index) => {
      const tierLevel = tier.split('_')[1];
      const isUserPlan = portalTiers.length ? portalTiers.includes(tier) : tier === Tier.HUBSPOT_FREE;
      return /*#__PURE__*/_jsxs(TH, {
        showBorder: index === 0,
        "aria-label": I18n.text(getTierI18nKey(tier)),
        children: [isUserPlan && isCustomer ? /*#__PURE__*/_jsx(View, {
          marginBlockStart: "-700",
          marginBlockEnd: "250",
          children: /*#__PURE__*/_jsx(Badge, {
            use: "default",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-common.compatibility.featureCompatibilityMatrix.yourPlan`
            })
          })
        }) : null, /*#__PURE__*/_jsx(FormattedMessage, {
          message: getTierLevelI18nKey(tierLevel)
        })]
      }, tier);
    }))]
  }), [featureType, isCustomer, portalTiers, tierGroupNames, TH, TR]);
  const renderedFeatureRows = useMemo(() => featuresWithTierCompatibility.map((feature, featureIndex) => /*#__PURE__*/_jsxs(TR, {
    index: featureIndex,
    children: [/*#__PURE__*/_jsx(TD, {
      className: "p-left-7",
      style: {
        backgroundColor: 'inherit',
        boxShadow: 'inset -3px 0 3px -3px #cbd6e2',
        left: '0',
        position: 'sticky',
        textAlign: 'start'
      },
      children: feature.text
    }), tierGroupNames.map(tierGroup => TierGroups[tierGroup].map((tier, index) => {
      const isFeatureCompatible = feature.tiers.includes(tier);
      return /*#__PURE__*/_jsx(TD, {
        showBorder: index === 0,
        children: isFeatureCompatible ? /*#__PURE__*/_jsx(SuccessIcon, {
          "data-test-id": `${tier}-feature-${featureIndex}-check`,
          color: "fill-positive-default"
        }) : null
      }, `${tier}-feature-${featureIndex}`);
    }))]
  }, `feature-row-${featureIndex}`)), [featuresWithTierCompatibility, tierGroupNames, TD, TR]);
  const renderedFooter = useMemo(() => /*#__PURE__*/_jsxs(TR, {
    index: featuresWithTierCompatibility.length,
    children: [/*#__PURE__*/_jsx(TD, {
      compact: true
    }), tierGroupNames.map(tierGroup => /*#__PURE__*/_jsx(TD, {
      colSpan: TierGroups[tierGroup].length,
      compact: true,
      showBorder: true,
      style: {
        fontSize: 12,
        whiteSpace: 'nowrap'
      },
      children: /*#__PURE__*/_jsx(Link, {
        "data-test-id": `${tierGroup}-pricing-link`,
        external: true,
        href: getPricingUrl(!isCustomer, tierGroup),
        onClick: () => {
          if (trackPricingLinkClick) {
            trackPricingLinkClick(tierGroup);
          }
        },
        children: tierGroup === 'CRM' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'marketplace-ui-common.compatibility.featureCompatibilityMatrix.seeFeatures'
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'marketplace-ui-common.compatibility.featureCompatibilityMatrix.seePricingAndFeatures'
        })
      })
    }, tierGroup))]
  }), [featuresWithTierCompatibility, isCustomer, tierGroupNames, trackPricingLinkClick, TD, TR]);
  return /*#__PURE__*/_jsxs(UITable, {
    ScrollContainer: ScrollContainerComponent,
    style: {
      textAlign: 'center'
    },
    children: [/*#__PURE__*/_jsxs("colgroup", {
      children: [/*#__PURE__*/_jsx("col", {
        style: {
          minWidth: 250
        }
      }), /*#__PURE__*/_jsx("col", {
        span: FLATTENED_TIER_ORDER.length
      })]
    }), /*#__PURE__*/_jsxs("tbody", {
      children: [renderedHubsHeader, renderedHubLevels, renderedFeatureRows, renderedFooter]
    })]
  });
}